'use client';

import { useState } from 'react';

/**
 * FilterPanel Component
 * Sidebar filters for property listings
 * 
 * @param {Object} props
 * @param {Object} props.filters - Current filter values
 * @param {function} props.onChange - Callback when filters change
 * @param {string} props.listingType - 'buy' | 'rent'
 */

// Filter options - API ready structure
const propertyTypes = [
    { value: 'apartment', label: 'Apartment' },
    { value: 'house', label: 'Independent House' },
    { value: 'villa', label: 'Villa' },
    // { value: 'plot', label: 'Plot/Land' },
    { value: 'commercial', label: 'Commercial' },
];

const bhkOptions = [
    { value: '1', label: '1 BHK' },
    { value: '2', label: '2 BHK' },
    { value: '3', label: '3 BHK' },
    { value: '4', label: '4 BHK' },
    { value: '5+', label: '5+ BHK' },
];

const postedByOptions = [
    { value: 'owner', label: 'Owner' },
    { value: 'agent', label: 'Agent/Broker' },
    { value: 'builder', label: 'Builder' },
];

const furnishingOptions = [
    { value: 'furnished', label: 'Fully Furnished' },
    { value: 'semi', label: 'Semi Furnished' },
    { value: 'unfurnished', label: 'Unfurnished' },
];

const statusOptions = [
    { value: 'ready', label: 'Ready to Move' },
    { value: 'construction', label: 'Under Construction' },
];

const budgetRanges = {
    buy: [
        { min: 0, max: 30, label: 'Under ₹30 Lac' },
        { min: 30, max: 50, label: '₹30-50 Lac' },
        { min: 50, max: 75, label: '₹50-75 Lac' },
        { min: 75, max: 100, label: '₹75 Lac - ₹1 Cr' },
        { min: 100, max: 200, label: '₹1-2 Cr' },
        { min: 200, max: null, label: 'Above ₹2 Cr' },
    ],
    rent: [
        { min: 0, max: 10000, label: 'Under ₹10K' },
        { min: 10000, max: 20000, label: '₹10K-20K' },
        { min: 20000, max: 35000, label: '₹20K-35K' },
        { min: 35000, max: 50000, label: '₹35K-50K' },
        { min: 50000, max: null, label: 'Above ₹50K' },
    ],

    civil: [
        { min: 0, max: 5000, label: 'Under ₹5,000' },
        { min: 5000, max: 20000, label: '₹5,000 - ₹20,000' },
        { min: 20000, max: 50000, label: '₹20,000 - ₹50,000' },
        { min: 50000, max: 100000, label: '₹50,000 - ₹1 Lakh' },
        { min: 100000, max: null, label: 'Above ₹1 Lakh' },
    ],
};

const civilServiceOptions = [
    { value: 'painting', label: 'Painting' },
    { value: 'plumbing', label: 'Plumbing' },
    { value: 'electrical', label: 'Electrical' },
    { value: 'carpentry', label: 'Carpentry' },
    { value: 'masonry', label: 'Masonry' },
    { value: 'renovation', label: 'Renovation' },
    { value: 'interior', label: 'Interior Design' },
];

const experienceOptions = [
    { value: '1-3', label: '1-3 Years' },
    { value: '3-5', label: '3-5 Years' },
    { value: '5+', label: '5+ Years' },
];

const FilterPanel = ({
    filters = {},
    onChange,
    listingType = 'buy',
    isMobile = false,
    onClose
}) => {
    const [localFilters, setLocalFilters] = useState(filters);

    const handleFilterChange = (key, value) => {
        const newFilters = { ...localFilters };

        // Handle array-based filters (multi-select)
        if (Array.isArray(newFilters[key])) {
            if (newFilters[key].includes(value)) {
                newFilters[key] = newFilters[key].filter(v => v !== value);
            } else {
                newFilters[key] = [...newFilters[key], value];
            }
        } else {
            newFilters[key] = value;
        }

        setLocalFilters(newFilters);
        if (onChange) onChange(newFilters);
    };

    const toggleArrayFilter = (key, value) => {
        const current = localFilters[key] || [];
        const updated = current.includes(value)
            ? current.filter(v => v !== value)
            : [...current, value];

        const newFilters = { ...localFilters, [key]: updated };
        setLocalFilters(newFilters);
        if (onChange) onChange(newFilters);
    };

    const clearFilters = () => {
        const cleared = {};
        setLocalFilters(cleared);
        if (onChange) onChange(cleared);
    };

    const activeFilterCount = Object.values(localFilters).filter(
        v => v && (Array.isArray(v) ? v.length > 0 : true)
    ).length;

    return (
        <div className={`filter-sidebar ${isMobile ? 'p-0' : ''}`}>
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-2">
                    <h3 className="font-semibold text-gray-900">Filters</h3>
                    {activeFilterCount > 0 && (
                        <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                            {activeFilterCount}
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearFilters}
                            className="text-sm text-primary hover:underline"
                        >
                            Clear All
                        </button>
                    )}
                    {isMobile && (
                        <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>
            </div>

            {/* Property Type (Hidden for Civil) */}
            {listingType !== 'civil' && (
                <div className="filter-section">
                    <h4 className="filter-title">Property Type</h4>
                    <div className="flex flex-wrap gap-2">
                        {propertyTypes.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => toggleArrayFilter('propertyType', type.value)}
                                className={`filter-chip ${(localFilters.propertyType || []).includes(type.value) ? 'active' : ''
                                    }`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Service Type (For Civil Only) */}
            {listingType === 'civil' && (
                <div className="filter-section">
                    <h4 className="filter-title">Service Type</h4>
                    <div className="flex flex-wrap gap-2">
                        {civilServiceOptions.map((type) => (
                            <button
                                key={type.value}
                                onClick={() => toggleArrayFilter('serviceType', type.value)}
                                className={`filter-chip ${(localFilters.serviceType || []).includes(type.value) ? 'active' : ''
                                    }`}
                            >
                                {type.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Experience (For Civil Only) */}
            {listingType === 'civil' && (
                <div className="filter-section">
                    <h4 className="filter-title">Experience</h4>
                    <div className="flex flex-wrap gap-2">
                        {experienceOptions.map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => toggleArrayFilter('experience', opt.value)}
                                className={`filter-chip ${(localFilters.experience || []).includes(opt.value) ? 'active' : ''
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* BHK (Hidden for Civil) */}
            {listingType !== 'civil' && (
                <div className="filter-section">
                    <h4 className="filter-title">BHK Type</h4>
                    <div className="flex flex-wrap gap-2">
                        {bhkOptions.map((bhk) => (
                            <button
                                key={bhk.value}
                                onClick={() => toggleArrayFilter('bhk', bhk.value)}
                                className={`filter-chip ${(localFilters.bhk || []).includes(bhk.value) ? 'active' : ''
                                    }`}
                            >
                                {bhk.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Budget */}
            <div className="filter-section">
                <h4 className="filter-title">Budget</h4>
                <div className="space-y-2">
                    {budgetRanges[listingType].map((range, idx) => (
                        <label key={idx} className="flex items-center gap-3 cursor-pointer group">
                            <input
                                type="radio"
                                name="budget"
                                checked={localFilters.budgetMin === range.min && localFilters.budgetMax === range.max}
                                onChange={() => {
                                    const newFilters = {
                                        ...localFilters,
                                        budgetMin: range.min,
                                        budgetMax: range.max,
                                    };
                                    setLocalFilters(newFilters);
                                    if (onChange) onChange(newFilters);
                                }}
                                className="w-4 h-4 text-primary focus:ring-primary border-gray-300"
                            />
                            <span className="text-sm text-gray-700 group-hover:text-primary">
                                {range.label}
                            </span>
                        </label>
                    ))}
                </div>
            </div>

            {/* Posted By */}
            <div className="filter-section">
                <h4 className="filter-title">Posted By</h4>
                <div className="flex flex-wrap gap-2">
                    {postedByOptions.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => toggleArrayFilter('postedBy', opt.value)}
                            className={`filter-chip ${(localFilters.postedBy || []).includes(opt.value) ? 'active' : ''
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Furnishing */}
            <div className="filter-section">
                <h4 className="filter-title">Furnishing</h4>
                <div className="flex flex-wrap gap-2">
                    {furnishingOptions.map((opt) => (
                        <button
                            key={opt.value}
                            onClick={() => toggleArrayFilter('furnishing', opt.value)}
                            className={`filter-chip ${(localFilters.furnishing || []).includes(opt.value) ? 'active' : ''
                                }`}
                        >
                            {opt.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Property Status (Buy only) */}
            {listingType === 'buy' && (
                <div className="filter-section">
                    <h4 className="filter-title">Status</h4>
                    <div className="flex flex-wrap gap-2">
                        {statusOptions.map((opt) => (
                            <button
                                key={opt.value}
                                onClick={() => toggleArrayFilter('status', opt.value)}
                                className={`filter-chip ${(localFilters.status || []).includes(opt.value) ? 'active' : ''
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Verified Only */}
            <div className="filter-section">
                <label className="flex items-center justify-between cursor-pointer">
                    <span className="text-sm font-medium text-gray-700">Verified Properties Only</span>
                    <div className="relative">
                        <input
                            type="checkbox"
                            checked={localFilters.verifiedOnly || false}
                            onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                            className="sr-only"
                        />
                        <div className={`w-11 h-6 rounded-full transition-colors ${localFilters.verifiedOnly ? 'bg-primary' : 'bg-gray-200'
                            }`}>
                            <div className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${localFilters.verifiedOnly ? 'translate-x-5' : 'translate-x-0'
                                }`} />
                        </div>
                    </div>
                </label>
            </div>

            {/* Mobile Apply Button */}
            {isMobile && (
                <div className="pt-4 border-t border-gray-200">
                    <button onClick={onClose} className="btn btn-primary w-full">
                        Apply Filters
                    </button>
                </div>
            )}
        </div>
    );
};

export default FilterPanel;
